/**
 * Schema.org JSON-LD builders.
 *
 * Rules (see docs/seo/STRUCTURED-DATA.md):
 *   • every node describes something VISIBLE on the page it is emitted on;
 *   • stable @id values from siteConfig.IDS — the Organization and WebSite
 *     nodes are referenced by @id from every page, never re-declared with
 *     different facts;
 *   • no ratings, reviews, counts, partners or outcomes — none are verified;
 *   • one @graph per page, assembled by buildGraph(meta).
 *
 * Pure functions: the prerender scripts and the Jest tests call them in Node.
 */
import {
  SITE_URL, SITE_NAME, SITE_ALIASES, SITE_DESCRIPTION, LOGO_URL, IDS, PARENT_ORG,
  CONTACT, SAME_AS, SISTER_PRODUCTS, LOCALE,
} from './siteConfig';
import { HERO as NEXUS_HERO, DIFFERENCE as NEXUS_DIFFERENCE, JOURNEY as NEXUS_JOURNEY } from '../BasicComponents/studentIntro/studentIntroContent';

const CONTEXT = 'https://schema.org';

export function organization() {
  return {
    '@type': 'Organization',
    '@id': IDS.organization,
    name: SITE_NAME,
    alternateName: SITE_ALIASES,
    url: `${SITE_URL}/`,
    logo: { '@type': 'ImageObject', url: LOGO_URL },
    description: SITE_DESCRIPTION,
    email: CONTACT.email,
    telephone: CONTACT.telephone,
    address: { '@type': 'PostalAddress', ...CONTACT.address },
    sameAs: SAME_AS,
    parentOrganization: {
      '@type': 'Organization',
      '@id': IDS.parentOrganization,
      name: PARENT_ORG.name,
      alternateName: PARENT_ORG.alternateName,
      url: PARENT_ORG.url,
    },
    brand: { '@type': 'Brand', name: 'TSG Nexus' },
    knowsAbout: [
      'Software engineering education',
      'Industry readiness',
      'Project-based learning',
      'Technical interview preparation',
      'Resume review',
    ],
  };
}

export function website() {
  return {
    '@type': 'WebSite',
    '@id': IDS.website,
    url: `${SITE_URL}/`,
    name: SITE_NAME,
    alternateName: SITE_ALIASES,
    inLanguage: 'en',
    publisher: { '@id': IDS.organization },
  };
}

export function webPage(meta, type = 'WebPage') {
  const node = {
    '@type': type,
    '@id': `${meta.canonical}#webpage`,
    url: meta.canonical,
    name: meta.ogTitle || meta.title,
    description: meta.description,
    inLanguage: 'en',
    isPartOf: { '@id': IDS.website },
    about: { '@id': IDS.organization },
  };
  if (meta.breadcrumbs && meta.breadcrumbs.length > 1) {
    node.breadcrumb = { '@id': `${meta.canonical}#breadcrumb` };
  }
  if (meta.ogImage) node.primaryImageOfPage = { '@type': 'ImageObject', url: meta.ogImage };
  return node;
}

export function aboutPage(meta) {
  return webPage(meta, 'AboutPage');
}

export function contactPage(meta) {
  return webPage(meta, 'ContactPage');
}

export function breadcrumbs(meta) {
  if (!meta.breadcrumbs || meta.breadcrumbs.length < 2) return null;
  return {
    '@type': 'BreadcrumbList',
    '@id': `${meta.canonical}#breadcrumb`,
    itemListElement: meta.breadcrumbs.map((crumb, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: crumb.name,
      item: crumb.path === '/' ? `${SITE_URL}/` : `${SITE_URL}${crumb.path}`,
    })),
  };
}

/**
 * TSG Nexus Product Engineering — modelled as a Course because the page
 * describes a structured learning-and-building programme with a price.
 * Every value below is printed on /tsg-product-engg:
 *   name/description → hero; teaches → the Journey steps; price → the
 *   Difference "value" card (₹9,999 + GST). No duration, workload, rating or
 *   outcome is claimed because none is printed.
 */
export function nexusProduct(meta) {
  const priceDigits = String(NEXUS_DIFFERENCE.value.price).replace(/[^\d.]/g, '');
  return {
    '@type': 'Course',
    '@id': IDS.nexusProduct,
    name: 'TSG Nexus Product Engineering',
    alternateName: ['TSG Nexus', 'TSG Nexus Product Engineering Experience'],
    description: NEXUS_HERO.lead,
    url: meta.canonical,
    inLanguage: 'en',
    provider: { '@id': IDS.organization },
    teaches: NEXUS_JOURNEY.steps.map((s) => s.name),
    educationalCredentialAwarded: undefined,
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'Online',
      instructor: undefined,
    },
    offers: priceDigits
      ? {
          '@type': 'Offer',
          category: 'Paid',
          price: priceDigits,
          priceCurrency: 'INR',
          description: `${NEXUS_DIFFERENCE.value.price} ${NEXUS_DIFFERENCE.value.tax} (Starter plan; GST added at checkout)`,
          url: meta.canonical,
        }
      : undefined,
    isRelatedTo: [
      { '@type': 'SoftwareApplication', name: SISTER_PRODUCTS.exihire.name, url: SISTER_PRODUCTS.exihire.url, applicationCategory: 'BusinessApplication', description: SISTER_PRODUCTS.exihire.description },
      { '@type': 'SoftwareApplication', name: SISTER_PRODUCTS.exitracker.name, url: SISTER_PRODUCTS.exitracker.url, applicationCategory: 'BusinessApplication', description: SISTER_PRODUCTS.exitracker.description },
    ],
  };
}

const BUILDERS = {
  organization: () => organization(),
  website: () => website(),
  webpage: (meta) => webPage(meta),
  aboutPage: (meta) => aboutPage(meta),
  contactPage: (meta) => contactPage(meta),
  breadcrumbs: (meta) => breadcrumbs(meta),
  nexusProduct: (meta) => nexusProduct(meta),
};

/** Drop undefined/null values recursively so the JSON is clean. */
export function compact(value) {
  if (Array.isArray(value)) return value.map(compact).filter((v) => v !== undefined && v !== null);
  if (value && typeof value === 'object') {
    const out = {};
    Object.keys(value).forEach((k) => {
      const v = compact(value[k]);
      if (v !== undefined && v !== null) out[k] = v;
    });
    return out;
  }
  return value;
}

/** Build the per-page @graph. Returns null for non-indexable routes. */
export function buildGraph(meta) {
  if (!meta || !meta.indexable || !meta.canonical) return null;
  const names = meta.schema && meta.schema.length ? meta.schema : ['organization', 'website', 'webpage'];
  const nodes = names
    .map((n) => (BUILDERS[n] ? BUILDERS[n](meta) : null))
    .filter(Boolean);
  if (!nodes.length) return null;
  return compact({ '@context': CONTEXT, '@graph': nodes });
}

export const OG_LOCALE = LOCALE;
